/**
 * Lane C0-PROPS: homes and trade on the plains (C0_TOWN_LAYOUT §2 rows 6, 10, 11, 26): the plain house, the long
 * house, the courtyard house, the stilt house, the shophouse (goods by trade), the warehouse (โรงคลัง), the stable
 * and the jail. Footprints in tiles as laid; heights within the layout's art-pixel budget; the front (door) on the
 * front-left face, which looks south over the roads. Light from the upper right.
 */
import { hash2 } from '@nine/shared';
import { box, brick, couplet, door, lantern, M, plaque, snap, wall, type Ramp, type Tex } from '../buildings';
import type { Sprite } from '../scenery';
import { boardWalls, faces, hipRoof, plasterWalls, plinthK, timber, trim, windows, type Rect, type Stage } from './common';
import { awning, blob, done, gableRoof, hipGable, K, rail, ROOF_TILES, signboard, stage, steps, type RoofColour } from './parts';

/** A front door with its couplets, plaque and a lantern on the front-left face at u (centre). */
function entrance(s: Stage, at: number, u: number, z0: number, h = 54, lamp = true): void {
  const d0 = u - 0.5;
  const d1 = u + 0.5;
  box(s.px, s.P, [d0 - 0.15, at, d1 + 0.15, at + 0.3], 0, z0, (uu, v, l) => snap(M.stone, l + 0.1, uu | 0, v | 0, 0.2));
  door(s.px, s.P, 'left', at, d0, d1, z0, z0 + h);
  couplet(s.px, s.P, 'left', at, d0 - 0.28, d0 - 0.12, z0 + 10, z0 + h - 4);
  couplet(s.px, s.P, 'left', at, d1 + 0.12, d1 + 0.28, z0 + 10, z0 + h - 4);
  plaque(s.px, s.P, 'left', at, d0 + 0.1, d1 - 0.1, z0 + h + 3, z0 + h + 11);
  if (lamp) {
    lantern(s.px, s.P(d0 - 0.45, at + 0.05, z0 + h + 12));
    lantern(s.px, s.P(d1 + 0.45, at + 0.05, z0 + h + 12));
  }
}

/** Firewood stacked against a wall at the front-right face (x = at), log ends out. */
function firewood(s: Stage, at: number, u: number, z0: number): void {
  for (let row = 0; row < 3; row++)
    for (let k = 0; k < 5 - row; k++) {
      const [lx, ly] = s.P(at + 0.1, u + k * 0.16 + row * 0.08, z0 + 3 + row * 5);
      for (let b = -2; b <= 2; b++)
        for (let a = -2; a <= 2; a++)
          if (a * a + b * b <= 5) s.px.set(lx + a, ly + b, a * a + b * b <= 1 ? M.deck[4]! : snap(M.deck, 0.45 - b * 0.08, lx + a, ly + b));
    }
}

/** A glazed water jar standing at a ground point. */
function jar(s: Stage, x: number, y: number, r: Ramp = M.terra, hgt = 12): void {
  const [jx, jy] = s.P(x, y, 0);
  for (let b = -hgt; b <= 0; b++)
    for (let a = -5; a <= 5; a++) {
      const rr = 5 - Math.abs(b + hgt / 2) * (8 / hgt) * 0.25 * 2;
      if (Math.abs(a) > rr) continue;
      s.px.set(jx + a, jy + b, snap(r, 0.5 + a * 0.07 - (b > -3 ? 0.15 : 0), jx + a, jy + b));
    }
  s.px.rect(jx - 3, jy - hgt - 1, 7, 1, r[1]!);
}

/**
 * The plain house (6 × 6, 136 px): a plinth, a brick footing, plaster in a timber frame, a front porch on two posts
 * under a lean-to, a hip roof with horn tips. `v` varies the details (firewood, jars, drying rack, roof ridge).
 */
export function plainHouse(w: number, h: number, colour: RoofColour, v: number): Sprite {
  const s = stage(w, h, 150);
  const tiles = ROOF_TILES[colour];
  const r: Rect = [0.4, 0.4, w - 0.4, h - 1.1];
  plinthK(s, [0.15, 0.15, w - 0.15, h - 0.15], 8);
  const eave = 82;
  plasterWalls(s, r, 8, eave, v % 2 ? 'brick' : 'stone');
  for (const side of ['left', 'right'] as const) {
    const [at, u0, u1] = side === 'left' ? [r[3], r[0], r[2]] : [r[2], r[1], r[3]];
    timber(s, side, at, u0, u1, 22, eave, 1.3, [24, 52, eave - 4], M.timber, eave);
    windows(s, side, at, u0, u1, 30, 50, side === 'left' ? 4 : 3, 0.42, eave, side === 'left' ? [w / 2 - 0.9, w / 2 + 0.9] : [0, -1]);
  }
  trim(s, r, eave - 10);
  entrance(s, r[3], w / 2, 8);
  // The porch: two posts and a lean-to skirt over the door.
  for (const u of [w / 2 - 1.2, w / 2 + 1.2]) box(s.px, s.P, [u - 0.1, h - 0.55, u + 0.1, h - 0.35], 8, 72, (uu, vv, l) => snap(M.lacquer, l + 0.05, uu | 0, vv | 0));
  hipRoof(s, [w / 2 - 1.6, r[3], w / 2 + 1.6, h + 0.05], 70, [w / 2 - 1.6, r[3], w / 2 + 1.6, r[3]], 80, tiles, { ridge: false, horns: 0.6 });
  if (v % 3 === 0) firewood(s, r[2], 0.8, 8);
  else jar(s, r[2] + 0.25, h - 1.6);
  const o = 0.6;
  const alongX = w >= h;
  hipRoof(
    s,
    [-o + 0.4, -o + 0.4, w + o - 0.4, h - 1.1 + o],
    eave - 2,
    alongX ? [1.6, (h - 0.7) / 2, w - 1.6, (h - 0.7) / 2] : [w / 2, 1.6, w / 2, h - 2.6],
    eave + 52,
    tiles,
    { tassels: v % 2 === 0 },
  );
  return done(s);
}

/** The long house (เรือนยาว, 14 × 6, 120 px): low walls, a veranda along the front under its own skirt, a gable roof. */
export function longHouse(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 132);
  const tiles = ROOF_TILES[colour];
  const r: Rect = [0.4, 0.4, w - 0.4, h - 1.8];
  plinthK(s, [0.15, 0.15, w - 0.15, h - 0.15], 7);
  const eave = 70;
  plasterWalls(s, r, 7, eave, 'brick');
  for (const side of ['left', 'right'] as const) {
    const [at, u0, u1] = side === 'left' ? [r[3], r[0], r[2]] : [r[2], r[1], r[3]];
    timber(s, side, at, u0, u1, 21, eave, 1.4, [22, eave - 4], M.timber, eave);
  }
  // Doors and bamboo blinds along the veranda wall.
  for (let k = 0; k < 4; k++) {
    const u = 1.6 + k * 3.4;
    door(s.px, s.P, 'left', r[3], u - 0.45, u + 0.45, 7, 54);
    wall(s.px, s.P, 'left', r[3], u + 0.8, u + 2.3, 26, 56, (uu, vv, l) => snap(K.straw, l + ((vv | 0) % 3 === 0 ? -0.2 : 0.05), uu | 0, vv | 0, 0));
  }
  windows(s, 'right', r[2], r[1], r[3], 28, 50, 2, 0.4, eave);
  // The veranda: a deck, posts and a rail at the ends.
  box(s.px, s.P, [0.4, h - 1.8, w - 0.4, h - 0.3], 7, 11, (u, v, l) => snap(M.deck, l, u | 0, v | 0, 0.2), (u, v, l) => snap(M.deck, l + ((u | 0) % 6 === 0 ? -0.2 : 0.05), u | 0, v | 0, 0.2));
  for (let k = 0; k <= 5; k++) {
    const u = 0.6 + ((w - 1.2) * k) / 5;
    box(s.px, s.P, [u - 0.09, h - 0.55, u + 0.09, h - 0.37], 11, 62, (uu, vv, l) => snap(M.wood, l + 0.05, uu | 0, vv | 0));
    if (k % 2) lantern(s.px, s.P(u + 1.1, h - 0.4, 60), 2);
  }
  rail(s.px, s.P, 'right', w - 0.4, h - 1.8, h - 0.3, 11, 12, M.wood);
  hipRoof(s, [0.2, h - 1.8, w - 0.2, h + 0.1], 58, [0.2, h - 1.8, w - 0.2, h - 1.8], 72, tiles, { ridge: false, horns: 0.5 });
  // Bags and a basket on the deck.
  blob(s.px, ...s.P(w - 2.2, h - 0.9, 16), 5, 4, K.straw, 0.55);
  gableRoof(s.px, s.P, [-0.2, -0.3, w + 0.2, h - 1.3], eave - 2, 118, tiles, plasterGable, M.stone, w - 0.45);
  return done(s);
}
const plasterGable: Tex = (u, v, l) => snap(M.plaster, l + ((u | 0) % 11 === 0 ? -0.25 : 0), u | 0, v | 0, 0.2);

/** The courtyard house (บ้านมีลานใน, 12 × 10, 136 px): a hall at the back, a wing on the west, a low wall and gate. */
export function courtyardHouse(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 150);
  const tiles = ROOF_TILES[colour];
  // The back hall across the whole width.
  const hr: Rect = [0.6, 0.5, w - 0.6, 3.8];
  plinthK(s, [0.4, 0.3, w - 0.4, 4.1], 10);
  plasterWalls(s, hr, 10, 80, 'stone');
  timber(s, 'left', hr[3], hr[0], hr[2], 24, 80, 1.6, [26, 76], M.timber, 80);
  windows(s, 'left', hr[3], hr[0], hr[2], 34, 58, 5, 0.45, 80, [w / 2 - 0.9, w / 2 + 0.9]);
  entrance(s, hr[3], w / 2, 10, 52);
  steps(s.px, s.P, 'left', 4.1, w / 2 - 1, w / 2 + 1, 10, 3);
  hipGable(s.px, s.P, [0.1, 0.0, w - 0.1, 4.4], 80, 1.3, 106, 134, tiles);
  // The west wing.
  const wr: Rect = [0.7, 4.6, 3.6, h - 1.2];
  plinthK(s, [0.5, 4.4, 3.8, h - 1.0], 6);
  plasterWalls(s, wr, 6, 62, 'none');
  timber(s, 'right', wr[2], wr[1], wr[3], 6, 62, 1.2, [20, 58], M.timber, 62);
  windows(s, 'right', wr[2], wr[1], wr[3], 22, 44, 3, 0.4, 62);
  hipRoof(s, [0.3, 4.3, 4.0, h - 0.8], 60, [2.15, 5.6, 2.15, h - 2.3], 96, tiles, { horns: 0.7 });
  // A well and a plum pot in the court.
  const [wx, wy] = s.P(7.6, 6.6, 0);
  for (let b = -3; b <= 3; b++)
    for (let a = -7; a <= 7; a++)
      if ((a * a) / 49 + (b * b) / 9 <= 1) s.px.set(wx + a, wy + b - 8, (a * a) / 25 + (b * b) / 4 <= 1 ? M.blue[1]! : snap(M.stone, 0.7 - b * 0.05, wx + a, wy + b));
  box(s.px, s.P, [7.2, 6.2, 8.0, 7.0], 0, 7, (u, v, l) => snap(M.stone, l, u | 0, v | 0, 0.2), () => null);
  // The compound wall on the two near sides, a gate in the south wall with its own little roof.
  const cw: Rect = [0.2, 0.2, w - 0.2, h - 0.2];
  const gate0 = w / 2 + 1.2;
  const gate1 = gate0 + 1.6;
  wall(s.px, s.P, 'left', cw[3], cw[0], gate0, 0, 40, wallTex);
  wall(s.px, s.P, 'left', cw[3], gate1, cw[2], 0, 40, wallTex);
  wall(s.px, s.P, 'right', cw[2], 4.2, cw[3], 0, 40, wallTex);
  hipRoof(s, [cw[0] - 0.05, cw[3] - 0.25, gate0, cw[3] + 0.25], 40, [cw[0], cw[3], gate0, cw[3]], 46, tiles, { ridge: false, horns: 0 });
  hipRoof(s, [gate1, cw[3] - 0.25, cw[2] + 0.05, cw[3] + 0.25], 40, [gate1, cw[3], cw[2], cw[3]], 46, tiles, { ridge: false, horns: 0 });
  hipRoof(s, [cw[2] - 0.25, 4.2, cw[2] + 0.25, cw[3]], 40, [cw[2], 4.2, cw[2], cw[3]], 46, tiles, { ridge: false, horns: 0 });
  // The gate: posts, red leaves, a roof.
  for (const u of [gate0, gate1]) box(s.px, s.P, [u - 0.12, cw[3] - 0.12, u + 0.12, cw[3] + 0.12], 0, 58, (uu, vv, l) => snap(M.lacquer, l, uu | 0, vv | 0));
  door(s.px, s.P, 'left', cw[3], gate0 + 0.12, gate1 - 0.12, 0, 46, M.lacquer);
  hipRoof(s, [gate0 - 0.4, cw[3] - 0.5, gate1 + 0.4, cw[3] + 0.5], 58, [gate0 + 0.1, cw[3], gate1 - 0.1, cw[3]], 72, tiles, { horns: 0.6, tassels: true });
  lantern(s.px, s.P(gate0 - 0.2, cw[3] + 0.3, 56));
  lantern(s.px, s.P(gate1 + 0.2, cw[3] + 0.3, 56));
  return done(s);
}
/** White compound wall: plaster over a dark footing. */
const wallTex: Tex = (u, v, l) =>
  v < 8 ? snap(M.stone, l - 0.05 + (hash2(u | 0, v | 0) - 0.5) * 0.1, u | 0, v | 0) : snap(K.wallPlaster, l - 0.06 - (v > 34 ? 0.3 : 0) - (v < 12 ? 0.15 : 0), u | 0, v | 0);

/** The stilt house (เรือนใต้ถุนสูง, 6 × 6, 120 px): posts, a shaded underfloor, a deck with a rail, a ladder. */
export function stiltHouse(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 132);
  const tiles = ROOF_TILES[colour];
  const fl = 30;
  // The underfloor in shade, with baskets and a pen.
  box(s.px, s.P, [0.5, 0.5, w - 0.5, h - 0.5], 0, 1, (u, v, l) => snap(M.dark, l, u | 0, v | 0), (u, v) => snap(K.mud, 0.25, u | 0, v | 0, 0.3));
  for (let i = 0; i < 4; i++)
    for (let j = 0; j < 4; j++) {
      const x = 0.7 + ((w - 1.4) * i) / 3;
      const y = 0.7 + ((h - 1.4) * j) / 3;
      box(s.px, s.P, [x - 0.12, y - 0.12, x + 0.12, y + 0.12], 0, fl, (u, v, l) => snap(M.wood, l - (i < 3 && j < 3 ? 0.15 : 0), u | 0, v | 0));
    }
  blob(s.px, ...s.P(2.2, 3.4, 6), 5, 4, K.straw, 0.4);
  blob(s.px, ...s.P(3.2, 2.6, 5), 4, 3, K.straw, 0.35);
  // The deck, the house on it.
  box(s.px, s.P, [0.3, 0.3, w - 0.3, h - 0.3], fl, fl + 4, (u, v, l) => snap(M.deck, l, u | 0, v | 0, 0.2), (u, v, l) => snap(M.deck, l + ((u | 0) % 6 === 0 ? -0.2 : 0), u | 0, v | 0, 0.2));
  const r: Rect = [0.7, 0.7, w - 0.7, h - 1.7];
  boardWalls(s, r, fl + 4, 84, M.wood, 88);
  timber(s, 'left', r[3], r[0], r[2], fl + 4, 84, 1.5, [fl + 6, 80], M.timber, 88);
  timber(s, 'right', r[2], r[1], r[3], fl + 4, 84, 1.5, [fl + 6, 80], M.timber, 88);
  windows(s, 'left', r[3], r[0], r[2], fl + 20, fl + 40, 3, 0.45, 88, [w / 2 - 0.6, w / 2 + 0.6]);
  windows(s, 'right', r[2], r[1], r[3], fl + 20, fl + 40, 2, 0.45, 88);
  door(s.px, s.P, 'left', r[3], w / 2 - 0.45, w / 2 + 0.45, fl + 4, fl + 46);
  rail(s.px, s.P, 'left', h - 0.3, 0.3, w / 2 - 0.6, fl + 4, 12, M.wood);
  rail(s.px, s.P, 'left', h - 0.3, w / 2 + 0.6, w - 0.3, fl + 4, 12, M.wood);
  rail(s.px, s.P, 'right', w - 0.3, 0.3, h - 0.3, fl + 4, 12, M.wood);
  // The ladder down the front.
  for (const u of [w / 2 - 0.4, w / 2 + 0.4]) {
    const [ax, ay] = s.P(u, h + 0.2, 0);
    const [bx, by] = s.P(u, h - 0.3, fl + 4);
    for (let k = 0; k <= 30; k++) {
      const f = k / 30;
      s.px.set(ax + (bx - ax) * f, ay + (by - ay) * f, M.wood[2]!);
      s.px.set(ax + (bx - ax) * f + 1, ay + (by - ay) * f, M.wood[4]!);
    }
  }
  for (let k = 1; k < 6; k++) {
    const f = k / 6;
    const [ax, ay] = s.P(w / 2 - 0.4, h + 0.2 - 0.5 * f, fl * f);
    const [bx] = s.P(w / 2 + 0.4, h + 0.2 - 0.5 * f, fl * f);
    for (let x = Math.round(ax); x <= bx; x++) s.px.set(x, ay, M.wood[3]!);
  }
  gableRoof(s.px, s.P, [-0.1, 0.0, w + 0.1, h - 0.9], 86, 118, tiles, (u, v, l) => snap(K.straw, l + ((v | 0) % 3 ? 0 : -0.2), u | 0, v | 0, 0.1), M.wood, w - 0.6);
  return done(s);
}

type Trade = 'medicine' | 'cloth' | 'books' | 'smith' | 'goods';
const TRADE: Record<string, Trade> = { หมอยา: 'medicine', ร้านผ้า: 'cloth', ตำรา: 'books', ตีเหล็ก: 'smith' };

/** The shop's front, open: a counter and the goods of its trade, dark inside. */
function shopFront(s: Stage, at: number, u0: number, u1: number, z0: number, z1: number, trade: Trade): void {
  wall(s.px, s.P, 'left', at, u0, u1, z0, z1, (u, v) => snap(M.dark, 0.35 + (v - z0) / (z1 - z0) * 0.4, u | 0, v | 0));
  const a = u0 * 16;
  // Goods on shelves behind the counter.
  wall(s.px, s.P, 'left', at, u0 + 0.1, u1 - 0.1, z0 + 22, z1 - 6, (u, v) => {
    const row = Math.floor((v - z0 - 22) / 10);
    const shelf = (v - z0 - 22) % 10 < 1.5;
    if (shelf) return M.wood[3]!;
    const k = Math.floor((u - a) / 5);
    const hh = hash2(k * 7 + row, row * 3 + 1);
    switch (trade) {
      case 'medicine': // drawers and jars
        return (u - a) % 8 < 1 || (v | 0) % 5 === 0 ? M.wood[1]! : (u | 0) % 8 === 4 && (v | 0) % 5 === 2 ? M.gold[3]! : snap(M.wood, 0.55, u | 0, v | 0, 0);
      case 'cloth': // bolts of colour
        return snap([M.red, K.indigo, K.ochre, M.jade, K.cream][Math.floor(hh * 5)]!, 0.55 + ((u | 0) % 5 === 0 ? -0.2 : 0), u | 0, v | 0, 0);
      case 'books': // scrolls and stacked books
        return snap(hh < 0.5 ? K.cream : M.paper, 0.5 + ((v | 0) % 3 === 0 ? -0.25 : 0.1), u | 0, v | 0, 0);
      case 'smith': // blades on a rack
        return (u | 0) % 4 === 0 ? snap(K.iron, 0.75 - (v - z0) * 0.004, u | 0, v | 0, 0) : (u | 0) % 4 === 1 ? K.iron[1]! : M.dark[1]!;
      default:
        return snap(hh < 0.5 ? K.straw : M.terra, 0.5, u | 0, v | 0, 0);
    }
  });
  // The counter.
  box(s.px, s.P, [u0 + 0.05, at, u1 - 0.05, at + 0.45], z0, z0 + 18, (u, v, l) => snap(M.wood, l + ((u | 0) % 7 === 0 ? -0.2 : 0), u | 0, v | 0), (u, v, l) => snap(M.deck, l + 0.1, u | 0, v | 0, 0));
  // Goods on the counter.
  for (let k = 0; k < 4; k++) {
    const [gx, gy] = s.P(u0 + 0.4 + k * ((u1 - u0 - 0.8) / 3), at + 0.22, z0 + 18);
    if (trade === 'medicine') blob(s.px, gx, gy - 3, 2, 3, k % 2 ? M.jade : M.terra, 0.6);
    else if (trade === 'cloth') blob(s.px, gx, gy - 2, 4, 2, [M.red, K.indigo, K.ochre, M.jade][k]!, 0.6);
    else if (trade === 'books') s.px.rect(gx - 3, gy - 3, 6, 3, K.cream[3]!);
    else if (trade === 'smith') s.px.rect(gx - 4, gy - 2, 8, 1, K.iron[4]!);
    else blob(s.px, gx, gy - 2, 3, 2, K.straw, 0.6);
  }
}

/**
 * The shophouse (6 × 6, 136 px, red): an open shop below under a cloth awning, a hanging signboard, a living storey
 * above with a little balcony rail, a hip roof. Goods by trade (the layout's name).
 */
export function shophouse(w: number, h: number, colour: RoofColour, name: string, v: number): Sprite {
  const trade = TRADE[name] ?? 'goods';
  const s = stage(w, h, 150);
  const tiles = ROOF_TILES[colour];
  const r: Rect = [0.4, 0.4, w - 0.4, h - 0.9];
  plinthK(s, [0.15, 0.15, w - 0.15, h - 0.15], 6);
  const z1 = 62;
  const eave = 104;
  plasterWalls(s, r, 6, z1, 'brick', z1 + 8);
  timber(s, 'right', r[2], r[1], r[3], 6, z1, 1.3, [z1 - 3], M.timber, z1 + 8);
  windows(s, 'right', r[2], r[1], r[3], 26, 48, 3, 0.45, z1 + 8);
  shopFront(s, r[3], r[0] + 0.5, r[2] - 0.5, 6, z1 - 4, trade);
  timber(s, 'left', r[3], r[0], r[2], 6, z1, 2.4, [z1 - 3], K.maroon, z1 + 8);
  // The forge's glow for the smith.
  if (trade === 'smith') {
    const [fx, fy] = s.P(r[2] - 1.2, r[3] + 0.05, 14);
    for (let b = -4; b <= 2; b++) for (let a = -5; a <= 5; a++) if (a * a + b * b * 2 < 26) s.px.set(fx + a, fy + b, snap(M.red, 0.95 - (a * a + b * b) * 0.03, a, b, 0));
  }
  // Skirt roof between the storeys.
  hipRoof(s, [r[0] - 0.35, r[1] - 0.35, r[2] + 0.35, r[3] + 0.35], z1, [r[0], r[1], r[2], r[3]], z1 + 8, tiles, { ridge: false, horns: 0.5 });
  const u: Rect = [0.6, 0.6, w - 0.6, h - 1.1];
  if (v % 2 === 1) boardWalls(s, u, z1 + 8, eave, M.wood, eave);
  else plasterWalls(s, u, z1 + 8, eave, 'none');
  timber(s, 'left', u[3], u[0], u[2], z1 + 8, eave, 1.2, [eave - 4], M.timber, eave);
  timber(s, 'right', u[2], u[1], u[3], z1 + 8, eave, 1.2, [eave - 4], M.timber, eave);
  windows(s, 'left', u[3], u[0], u[2], z1 + 14, eave - 10, 4, 0.5, eave);
  windows(s, 'right', u[2], u[1], u[3], z1 + 14, eave - 10, 3, 0.5, eave);
  rail(s.px, s.P, 'left', u[3] + 0.25, u[0], u[2], z1 + 8, 10, K.maroon);
  plaque(s.px, s.P, 'left', r[3] + 0.36, w / 2 - 1.1, w / 2 + 1.1, z1 - 2, z1 + 8);
  // The awning and the signboard.
  awning(s.px, s.P, 'left', r[3], r[0] + 0.3, r[2] - 0.3, 58, 0.7, 9, v % 2 ? K.indigo : M.red, K.cream);
  signboard(s.px, s.P, 'right', r[2] + 0.02, r[3] - 0.4, z1 - 4, 36);
  lantern(s.px, s.P(r[0] + 0.2, h - 0.2, 60));
  lantern(s.px, s.P(r[2] - 0.2, h - 0.2, 60));
  const o = 0.55;
  hipRoof(s, [-o + 0.6, -o + 0.6, w + o - 0.6, h - 1.1 + o], eave - 2, [1.7, (h - 0.5) / 2, w - 1.7, (h - 0.5) / 2], 134, tiles, { tassels: true });
  return done(s);
}

/** The warehouse (โรงคลัง, 6 × 6, 136 px, grey): brick, a big studded door, high vents, a gable roof, goods stacked. */
export function warehouse(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 150);
  const tiles = ROOF_TILES[colour];
  const r: Rect = [0.35, 0.35, w - 0.35, h - 0.9];
  plinthK(s, [0.15, 0.15, w - 0.15, h - 0.15], 10);
  const eave = 92;
  faces(s, r, 10, eave, brick(eave + 6));
  faces(s, r, 60, eave, (u, v, l) => snap(M.plaster, l + 0.05 - (v > eave - 6 ? 0.3 : 0), u | 0, v | 0));
  trim(s, r, 56, M.dark);
  // High vents (iron-barred), the big door with iron bands, the official plaque.
  for (const side of ['left', 'right'] as const) {
    const [at, u0, u1] = side === 'left' ? [r[3], r[0], r[2]] : [r[2], r[1], r[3]];
    const n = 3;
    for (let k = 0; k < n; k++) {
      const c = u0 + ((u1 - u0) * (k + 0.5)) / n;
      if (side === 'left' && Math.abs(c - w / 2) < 1) continue;
      wall(s.px, s.P, side, at, c - 0.3, c + 0.3, 68, 82, (u, v) => ((u | 0) % 3 === 0 ? K.iron[3]! : snap(M.dark, 0.3 + (v - 68) / 40, u | 0, v | 0, 0)));
    }
  }
  wall(s.px, s.P, 'left', r[3], w / 2 - 1.05, w / 2 + 1.05, 10, 68, (u, v, l) => snap(M.timber, l - 0.1, u | 0, v | 0));
  door(s.px, s.P, 'left', r[3], w / 2 - 0.9, w / 2 + 0.9, 10, 64, M.timber);
  wall(s.px, s.P, 'left', r[3] + 0.01, w / 2 - 0.9, w / 2 + 0.9, 24, 27, (u) => ((u | 0) % 7 === 3 ? M.gold[3]! : K.iron[2]!));
  wall(s.px, s.P, 'left', r[3] + 0.01, w / 2 - 0.9, w / 2 + 0.9, 48, 51, (u) => ((u | 0) % 7 === 3 ? M.gold[3]! : K.iron[2]!));
  plaque(s.px, s.P, 'left', r[3] + 0.02, w / 2 - 0.9, w / 2 + 0.9, 70, 82, M.blue);
  // Crates and sacks by the door.
  for (const [x, y, z] of [
    [w - 1.2, h - 0.5, 0],
    [w - 0.75, h - 0.5, 0],
    [w - 1.0, h - 0.5, 12],
  ] as const)
    box(s.px, s.P, [x - 0.2, y - 0.25, x + 0.2, y + 0.15], z, z + 12, (u, v, l) => snap(M.deck, l + ((u | 0) % 6 === 0 || (v | 0) % 6 === 0 ? -0.25 : 0), u | 0, v | 0, 0));
  blob(s.px, ...s.P(0.9, h - 0.5, 5), 5, 5, K.cream, 0.55);
  blob(s.px, ...s.P(1.4, h - 0.45, 5), 5, 5, K.cream, 0.5);
  lantern(s.px, s.P(w / 2 - 1.3, r[3] + 0.05, 74));
  lantern(s.px, s.P(w / 2 + 1.3, r[3] + 0.05, 74));
  gableRoof(s.px, s.P, [-0.15, -0.1, w + 0.15, h - 0.5], eave, 134, tiles, brick(1e9), M.stone, r[2]);
  return done(s);
}

/** The stable (คอกม้า, 8 × 6, 90 px, grey): an open shed of posts, a board back wall, stalls, hay, a trough. */
export function stable(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 100);
  const tiles = ROOF_TILES[colour];
  box(s.px, s.P, [0.2, 0.2, w - 0.2, h - 0.2], 0, 2, (u, v, l) => snap(K.mud, l, u | 0, v | 0, 0.3), (u, v) => snap(K.straw, 0.35 + hash2(u | 0, v | 0) * 0.2, u | 0, v | 0, 0.3));
  // The back walls (seen through the open front).
  wall(s.px, s.P, 'left', 0.5, 0.4, w - 0.4, 2, 58, (u, v, l) => snap(M.wood, 0.36 + ((u | 0) % 5 === 0 ? -0.15 : 0) - (v > 52 ? 0.12 : 0) + l * 0, u | 0, v | 0));
  wall(s.px, s.P, 'right', 0.5, 0.5, h - 0.4, 2, 58, (u, v) => snap(M.wood, 0.28 + ((u | 0) % 5 === 0 ? -0.15 : 0), u | 0, v | 0));
  // Hay racks on the back wall, stall partitions, a trough along the front.
  for (let k = 0; k < 3; k++) {
    const u0 = 0.8 + k * ((w - 1.6) / 3);
    wall(s.px, s.P, 'left', 0.55, u0 + 0.3, u0 + (w - 1.6) / 3 - 0.3, 26, 40, (u, v) => ((u | 0) % 3 === 0 ? M.wood[1]! : snap(K.straw, 0.5 + hash2(u | 0, v | 0) * 0.3, u | 0, v | 0, 0)));
    if (k) box(s.px, s.P, [u0 - 0.06, 0.5, u0 + 0.06, h - 1.6], 2, 30, (u, v, l) => snap(M.wood, l, u | 0, v | 0));
  }
  box(s.px, s.P, [0.8, h - 1.4, w - 0.8, h - 1.0], 2, 12, (u, v, l) => snap(M.wood, l - 0.05, u | 0, v | 0), (u, v) => snap(M.blue, 0.4, u | 0, v | 0, 0));
  blob(s.px, ...s.P(w - 1.0, h - 0.6, 6), 8, 5, K.straw, 0.6);
  for (let i = 0; i < 4; i++) {
    const x = 0.4 + ((w - 0.8) * i) / 3;
    box(s.px, s.P, [x - 0.11, h - 0.6, x + 0.11, h - 0.38], 0, 60, (u, v, l) => snap(M.wood, l, u | 0, v | 0));
  }
  box(s.px, s.P, [w - 0.6, 0.4, w - 0.38, 0.62], 0, 60, (u, v, l) => snap(M.wood, l, u | 0, v | 0));
  lantern(s.px, s.P(w / 2, h - 0.45, 58), 2);
  gableRoof(s.px, s.P, [-0.1, 0.0, w + 0.1, h + 0.1], 58, 88, tiles, (u, v, l) => snap(M.wood, l + ((u | 0) % 5 === 0 ? -0.2 : 0), u | 0, v | 0), M.wood, w - 0.4);
  return done(s);
}

/** The jail (คุก, 8 × 4, 90 px, grey): grey brick, a barred cell front, a guard's lamp, a gable roof. */
export function jail(w: number, h: number, colour: RoofColour): Sprite {
  const s = stage(w, h, 100);
  const tiles = ROOF_TILES[colour];
  const r: Rect = [0.3, 0.3, w - 0.3, h - 0.5];
  plinthK(s, [0.15, 0.15, w - 0.15, h - 0.15], 6, false);
  faces(s, r, 6, 60, brick(66));
  // The cell: a dark opening with heavy wooden bars.
  wall(s.px, s.P, 'left', r[3], 1.2, w - 2.6, 8, 48, (u, v) => ((u | 0) % 5 < 2 ? snap(M.timber, 0.5, u | 0, v | 0, 0) : snap(M.dark, 0.2 + (v - 8) / 120, u | 0, v | 0, 0)));
  wall(s.px, s.P, 'left', r[3], 1.2, w - 2.6, 26, 28, () => M.timber[2]!);
  door(s.px, s.P, 'left', r[3], w - 2.2, w - 1.2, 6, 48, M.timber);
  wall(s.px, s.P, 'right', r[2], 1.2, 2.4, 30, 42, (u) => ((u | 0) % 3 === 0 ? K.iron[3]! : M.dark[1]!));
  lantern(s.px, s.P(w - 0.8, r[3] + 0.05, 52));
  gableRoof(s.px, s.P, [-0.15, -0.1, w + 0.15, h + 0.05], 60, 88, tiles, brick(1e9), M.stone, r[2]);
  return done(s);
}
